import React from 'react'

import Button from '@material-ui/core/Button'
import { withStyles } from '@material-ui/core/styles'
import TextField from '@material-ui/core/TextField'
import ChangeHistory from '@material-ui/icons/ChangeHistory'
import CheckIcon from '@material-ui/icons/Check'
import ClearIcon from '@material-ui/icons/Clear'
import CloseIcon from '@material-ui/icons/Close'
import IconCloudDownload from '@material-ui/icons/CloudDownload'
import RubbishBinIcon from '@material-ui/icons/DeleteForever'
import EditIcon from '@material-ui/icons/Edit'
import ScatterPlot from '@material-ui/icons/ScatterPlot'
import TextFields from '@material-ui/icons/TextFields'
import Timeline from '@material-ui/icons/Timeline'
import get from 'lodash/get'
import isNil from 'lodash/isNil'
import toLower from 'lodash/toLower'
import moment from 'moment'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'

import { CONFIG_DRAWER_TAB_LOADED_PATH, LAYOUT_DRAWER_TABS_TOOLS_ANNOTATIONS_EXPANDED } from 'api/actions'
import { HistoryUpdate } from 'api/history'
import * as Model from 'api/model'
import * as PoziUtils from 'api/utils'
import AccordionWrapper from 'drawer/tabs/AccordionWrapper'
import { endAnnotation as endAnnotationAction, startAnnotation as startAnnotationAction } from 'drawer/tabs/layers/annotations/actions'
import DrawAnnotations from 'drawer/tabs/layers/annotations/DrawAnnotations'
import { PANEL_TITLE_DRAW } from 'drawer/tabs/layers/panels/LayerPanels'
import GetGeoJSON from 'drawer/tabs/layers/utils/GetGeoJSON'
import Downloader from 'drawer/tabs/tools/DownloadMap/Downloader'
import ColourPicker from 'utils/ColourPicker'

// https://openlayers.org/en/latest/apidoc/module-ol_interaction_Draw.html
export const POINT = 'Point'
export const LINE_STRING = 'LineString'
export const POLYGON = 'Polygon'
export const POINT_LABEL = 'PointLabel' // Actually a Point type but used to add a label.
export const REMOVE = 'REMOVE' // Custom type for removing features
export const SKETCH_POINT = 'SKETCH_POINT' // Custom type for drawing sketch points

const styles = theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    paddingLeft: 26,
    paddingBottom: 10,
    rowGap: 10
  },
  activeButton: {
    backgroundColor: 'lightgrey',
    color: '#515151',
    flex: '0 0 13em',
    justifyContent: 'flex-start',
    textTransform: 'none'
  },
  clearButtonWrapper: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: 20,
    rowGap: 10
  },
  clearButtonWrapperEmpty: {
  },
  closeButton: {
    margin: 0,
    minWidth: 36,
    padding: 0
  },
  clearAllButton: {
    backgroundColor: '#cd5c5c',
    color: 'lightGrey',
    flex: '0 0 13em',
    justifyContent: 'flex-start',
    textTransform: 'none'
  },
  closeButtonIcon: {
    color: 'lightgrey'
  },
  downloadButton: {
    color: '#737373',
    flex: '0 0 13em',
    justifyContent: 'flex-start',
    textTransform: 'none'
  },
  leftIcon: {
    marginRight: theme.spacing(1)
  },
  removeItemButton: {
    color: '#f08080',
    flex: '0 0 13em',
    justifyContent: 'flex-start',
    textTransform: 'none'
  },
  removeItemActiveButton: {
    backgroundColor: '#f08080',
    color: 'lightGrey',
    flex: '0 0 13em',
    justifyContent: 'flex-start',
    textTransform: 'none'
  },
  selectorButton: {
    color: '#737373',
    flex: '0 0 13em',
    justifyContent: 'flex-start',
    textTransform: 'none'
  },
  textField: {
    flex: '0 0 13em'
  },
  textFieldWrapper: {
    display: 'flex'
  },
  toggleContainer: {
    columnGap: 10,
    display: 'flex',
    flex: 1
  }
})

class LayerPanelEdit extends React.Component {
  constructor (props) {
    super(props)
    this.pure = {}
    this.pure.title = PANEL_TITLE_DRAW
    this.pure.headerIcon = <EditIcon /> // If you change this, change it in New.js
    this.pure.handleExpansionPanelChange = this.endAnnotationsOnPanelOpenClose.bind(this)

    const hasAnnotations = !!Model.get('app.state.annotation')

    this.state = {
      colour: '#0062b1', // gentle blue
      displayColourPicker: false,
      hasAnnotations
    }
  }

  componentDidMount () {
    const { endAnnotation } = this.props
    Model.on('app.state.tab', value => {
      console.log('End edits.')
      endAnnotation()
    })

    const self = this
    Model.on('app.state.annotation', annotation => {
      self.setState({
        hasAnnotations: !!annotation
      })
    })
  }

  componentDidUpdate (prevProps) {
    const { expanded, annotationType, endAnnotation } = this.props
    if (expanded !== prevProps.expanded && !expanded && annotationType !== '') {
      endAnnotation()
    }
  }

  // Stop any current annotations if a panel open or closes.
  endAnnotationsOnPanelOpenClose () {
    const { toggleExpansionPanel, annotationType, endAnnotation } = this.props
    if (annotationType !== '') {
      endAnnotation()
    }
    toggleExpansionPanel()
  }

  setSelectedAnnotationType (event) {
    const { startAnnotation, endAnnotation, layer } = this.props
    const type = get(event, 'currentTarget.id', '')
    const titleid = layer.titleid
    const colour = this.state.colour

    if (isNil(type)) {
      endAnnotation()
    } else {
      startAnnotation({ titleid, type, label: '', colour })
    }
  }

  setLabel (event) {
    const { startAnnotation, layer, type } = this.props
    const titleid = layer.titleid
    const label = event.target.value
    const colour = this.state.colour
    startAnnotation({ titleid, type, label, colour })
  }

  clearAnnotations = () => {
    const { layer: layerConf } = this.props
    const { titleid } = layerConf || {}
    const olLayer = PoziUtils.getOpenLayersObjectFromTitleID(titleid)

    olLayer?.getSource()?.clear()
    Model.unset('app.state.annotation')
    HistoryUpdate()
  }

  setColour = (colour) => {
    console.info('Colour change', colour)
    this.setState({ colour })

    const { startAnnotation, label, titleid, type } = this.props
    startAnnotation({ titleid, type, label, colour })
  }

  downloadGeoJSON = () => {
    const { layer } = this.props
    const geojsonString = GetGeoJSON(layer.titleid)
    const filename = 'draw_' + moment().format('YYYYMMDDHHmmss') + '.geojson'
    Downloader(geojsonString, filename)
  }

  render () {
    const { classes, toggleExpansionPanel, expanded, bToolsTabLoaded, layer, titleid, type, label, map, endAnnotation } = this.props
    const { hasAnnotations } = this.state

    if (!bToolsTabLoaded) {
      return null
    }

    const layerType = toLower(get(layer, 'type', ''))
    if (layerType !== 'geojson') {
      return null
    }

    const bLayerEditableFlag = get(layer, 'edit', false)
    if (!bLayerEditableFlag) {
      return null
    }

    // Only draw annotations on one layer at a time.
    // Essentially this solves the issue where the toggle button was selected for all layers.
    let toggleButtonGroupType = type
    if (layer.titleid !== titleid) {
      toggleButtonGroupType = ''
    }

    const closeButton = (
      <Button size="small" className={classes.closeButton} aria-label="Close" onClick={endAnnotation}>
        <CloseIcon className={classes.closeButtonIcon} />
      </Button>
    )

    const colourPicker = (
      <ColourPicker colour={this.state.colour} onChange={ this.setColour }/>
    )

    let displayLabel = label
    let disabled = false
    if (isNil(toggleButtonGroupType) || toggleButtonGroupType === '') {
      disabled = true
      displayLabel = ''
    }

    const autoFocus = toggleButtonGroupType === POINT_LABEL
    const placeholder = toggleButtonGroupType === POINT_LABEL ? 'Enter text' : 'Add label (optional)'
    const bIsDrawing = !!toggleButtonGroupType && toggleButtonGroupType !== REMOVE

    return (
      <AccordionWrapper
        expanded={expanded}
        onChange={toggleExpansionPanel}
        headerIcon={this.pure.headerIcon}
        title={this.pure.title}
        bExcludeExpandIcon={false}
      >
        <div className={classes.root}>
          <div className={classes.toggleContainer}>
            <Button id={POINT_LABEL} variant="outlined" className={toggleButtonGroupType === POINT_LABEL ? classes.activeButton : classes.selectorButton} onClick={this.setSelectedAnnotationType.bind(this)}>
              <TextFields className={classes.leftIcon} />
              Add text
            </Button>
            {toggleButtonGroupType === POINT_LABEL && (
              <React.Fragment>
                {colourPicker}
                {closeButton}
              </React.Fragment>
            )}
          </div>
          <div className={classes.toggleContainer}>
            <Button id={POINT} variant="outlined" className={toggleButtonGroupType === POINT ? classes.activeButton : classes.selectorButton} onClick={this.setSelectedAnnotationType.bind(this)}>
              <ScatterPlot className={classes.leftIcon} />
              Add point
            </Button>
            {toggleButtonGroupType === POINT && (
              <React.Fragment>
                {colourPicker}
                {closeButton}
              </React.Fragment>
            )}
          </div>
          <div className={classes.toggleContainer}>
            <Button id={LINE_STRING} variant="outlined" className={toggleButtonGroupType === LINE_STRING ? classes.activeButton : classes.selectorButton} onClick={this.setSelectedAnnotationType.bind(this)}>
              <Timeline className={classes.leftIcon} />
              Add line
            </Button>
            {toggleButtonGroupType === LINE_STRING && (
              <React.Fragment>
                {colourPicker}
                {closeButton}
              </React.Fragment>
            )}
          </div>
          <div className={classes.toggleContainer}>
            <Button id={POLYGON} variant="outlined" className={toggleButtonGroupType === POLYGON ? classes.activeButton : classes.selectorButton} onClick={this.setSelectedAnnotationType.bind(this)}>
              <ChangeHistory className={classes.leftIcon} />
              Add shape
            </Button>
            {toggleButtonGroupType === POLYGON && (
              <React.Fragment>
                {colourPicker}
                {closeButton}
              </React.Fragment>
            )}
          </div>
          {hasAnnotations && (
            <div className={classes.toggleContainer}>
              <Button id={REMOVE} variant="outlined" className={toggleButtonGroupType === REMOVE ? classes.removeItemActiveButton : classes.removeItemButton} onClick={this.setSelectedAnnotationType.bind(this)} >
                <ClearIcon className={classes.leftIcon} />
                Remove item
              </Button>
              {toggleButtonGroupType === REMOVE ? closeButton : null}
            </div>
          )}

          {bIsDrawing && (
            <form noValidate autoComplete="off" onSubmit={e => e.preventDefault()} className={classes.textFieldWrapper}>
              <TextField
                placeholder={placeholder}
                disabled={disabled}
                autoFocus={autoFocus}
                inputProps={{ 'data-lpignore': true }} // Hide LastPass icon

                // Hack to make sure autoFocus always works
                inputRef={(input) => {
                  if (autoFocus && input?.focus) {
                    input.focus()
                  }
                }}
                autoComplete="off"
                spellCheck="false"
                id="edit-label"
                className={classes.textField}
                value={displayLabel}
                onChange={this.setLabel.bind(this)}
                margin="normal"
                variant="outlined"
                size="small"
              />
            </form>
          )}

          <div className={bIsDrawing || hasAnnotations ? classes.clearButtonWrapper : classes.clearButtonWrapperEmpty}>
            {bIsDrawing && (
              <div className={classes.toggleContainer}>
                <Button variant="outlined" className={classes.selectorButton} onClick={endAnnotation}>
                  <CheckIcon className={classes.leftIcon} />
                  Finish
                </Button>
              </div>
            )}

            {!bIsDrawing && hasAnnotations && (
              <div className={classes.toggleContainer}>
                <Button variant="outlined" className={classes.clearAllButton} onClick={this.clearAnnotations} >
                  <RubbishBinIcon className={classes.leftIcon} />
                  Clear all
                </Button>
              </div>
            )}

            {!bIsDrawing && hasAnnotations && (
              <div className={classes.toggleContainer}>
                <Button className={classes.downloadButton} variant="outlined" onClick={this.downloadGeoJSON}>
                  <IconCloudDownload className={classes.leftIcon} />
                  Download
                </Button>
              </div>
            )}
          </div>

          <DrawAnnotations map={map} />
        </div>

      </AccordionWrapper>)
  }
}

LayerPanelEdit.propTypes = {
  classes: PropTypes.object.isRequired,
  map: PropTypes.object.isRequired,
  layer: PropTypes.object.isRequired,
  titleid: PropTypes.string.isRequired,
  startAnnotation: PropTypes.func.isRequired,
  endAnnotation: PropTypes.func.isRequired,
  type: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  bToolsTabLoaded: PropTypes.bool.isRequired,
  toggleExpansionPanel: PropTypes.func.isRequired,
  enabled: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  expanded: PropTypes.bool.isRequired,
  annotationType: PropTypes.string
}

function mapStateToProps (state, ownProps) {
  const toolsTabLoadedPath = CONFIG_DRAWER_TAB_LOADED_PATH + '.tools'
  const bToolsTabLoaded = get(state.config, toolsTabLoadedPath, false)
  const layer = state.layers[ownProps.titleid]
  const titleid = get(state, 'annotationInfo.titleid', '')
  const type = get(state, 'annotationInfo.type', '')
  const label = get(state, 'annotationInfo.label', '')
  const enabled = get(state, 'config.layout.drawer.tabs.tools.annotations.visible', true) // See templates.json
  const title = get(state, 'config.layout.drawer.tabs.tools.annotations.title', 'Print/Save') // See templates.json
  const expanded = get(state, 'config.' + LAYOUT_DRAWER_TABS_TOOLS_ANNOTATIONS_EXPANDED, false)
  const annotationType = get(state, 'annotationInfo.type', '')

  return {
    annotationType,
    enabled,
    title,
    expanded,
    bToolsTabLoaded,
    layer,
    titleid,
    type,
    label
  }
}

const mapDispatchToProps = dispatch => {
  return {
    startAnnotation: annotationInfo => {
      dispatch(startAnnotationAction(annotationInfo))
    },
    endAnnotation: () => {
      dispatch(endAnnotationAction())
    }
  }
}

export default (
  withStyles(styles)(
    connect(
      mapStateToProps,
      mapDispatchToProps
    )(LayerPanelEdit)
  )
)
